import { useState, useEffect, useCallback } from 'react';
import { 
  BillInvoice, 
  EnergyDataPoint, 
  InAppNotification, 
  UserAccount,
  ContractedPlace 
} from './types/energy';
import { 
  getLocalSyncedBundle, 
  saveLocalSyncedBundle, 
  selectContractedPlace, 
  getMillisecondsUntilNext1AM, 
  getNext1AMDisplayString, 
  hasMissed1AMSyncToday,
  getSqliteSyncedBundle
} from './services/localSyncStorage';
import { getStoredNotifications, saveStoredNotifications, dispatchPushNotification } from './services/notificationService';
import { getBiometricConfig } from './services/biometricService';
import { executePortalScrape, importLocalTelemetry, ScrapeExecutionResult } from './services/scraperEngine';
import { Language, translations } from './i18n/translations';
import { getSqliteLanguage, setSqliteLanguage, resetSqliteDatabase, getSqliteInitFailure } from './services/sqliteDatabase';

import { TopNav } from './components/TopNav';
import { BottomTabBar, TabDestination } from './components/BottomTabBar';
import { UsageView } from './components/UsageView';
import { BillingView } from './components/BillingView';
import { ScraperView } from './components/ScraperView';
import { NotificationCenter } from './components/NotificationCenter';
import { SettingsView } from './components/SettingsView';
import { BiometricModal } from './components/BiometricModal';
import { PWAInstallModal } from './components/PWAInstallModal';

import { WifiOff, ShieldAlert, CheckCircle2 } from 'lucide-react';

export default function App() {
  const [lang, setLang] = useState<Language>('ja');
  const [showInstallModal, setShowInstallModal] = useState(false);

  // Hydrate language and energy bundle from SQLite WASM on startup
  useEffect(() => {
    let active = true;

    getSqliteLanguage().then((saved) => {
      if (active && (saved === 'en' || saved === 'ja')) {
        setLang(saved);
      }
    }).catch(() => {});

    getSqliteSyncedBundle().then((sqliteBundle) => {
      if (!active) return;
      setBundle(sqliteBundle);
      setAccount(sqliteBundle.account);
      setPlaces(sqliteBundle.places);
      setSelectedPlaceId(sqliteBundle.selectedPlaceId);
      setInvoices(sqliteBundle.invoices);
      setHourlyData(sqliteBundle.hourlyUsage);
      setDailyData(sqliteBundle.dailyUsage);
      setMonthlyData(sqliteBundle.monthlyUsage);
      setLastSyncAt(sqliteBundle.lastSyncAt);
      // An empty contract and a device store that never opened render the same blank screen.
      setBootState(getSqliteInitFailure() ? 'error' : 'ready');
    }).catch((err) => {
      if (!active) return;
      console.error('[Boot] Failed to restore the locally synced bundle', err);
      setBootState('error');
    });

    return () => {
      active = false;
    };
  }, []);

  const t = translations[lang];

  // Load persistent local synced bundle (100% offline capable)
  const [bundle, setBundle] = useState(() => getLocalSyncedBundle());
  const [account, setAccount] = useState<UserAccount>(bundle.account);
  const [places, setPlaces] = useState<ContractedPlace[]>(bundle.places);
  const [selectedPlaceId, setSelectedPlaceId] = useState<string>(bundle.selectedPlaceId);
  const [invoices, setInvoices] = useState<BillInvoice[]>(bundle.invoices);
  const [hourlyData, setHourlyData] = useState<EnergyDataPoint[]>(bundle.hourlyUsage);
  const [dailyData, setDailyData] = useState<EnergyDataPoint[]>(bundle.dailyUsage);
  const [monthlyData, setMonthlyData] = useState<EnergyDataPoint[]>(bundle.monthlyUsage);
  const [notifications, setNotifications] = useState<InAppNotification[]>(getStoredNotifications());
  const [lastSyncAt, setLastSyncAt] = useState<string>(bundle.lastSyncAt);

  const [activeTab, setActiveTab] = useState<TabDestination>('usage');
  const [isOnline, setIsOnline] = useState<boolean>(() => typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [autoSyncToast, setAutoSyncToast] = useState<string | null>(null);
  const [bootState, setBootState] = useState<'loading' | 'ready' | 'error'>('loading');

  // Biometrics State
  const [isLocked, setIsLocked] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [biometricReason, setBiometricReason] = useState<{ title: string; subtitle: string }>({
    title: t.bioVerifyTitle,
    subtitle: t.bioVerifyDesc
  });

  // Track online / offline network status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check biometric lock on initial mount if enabled
  useEffect(() => {
    const config = getBiometricConfig();
    if (config.enabled) {
      setIsLocked(true);
      setShowBiometricPrompt(true);
    }
  }, []);

  const handleSyncComplete = useCallback(async (result: ScrapeExecutionResult) => {
    if (result.account) setAccount(result.account);
    if (result.places) setPlaces(result.places);
    const activePlaceId = result.account?.currentPlaceId || selectedPlaceId;
    setSelectedPlaceId(activePlaceId);
    if (result.invoices && result.invoices.length > 0) setInvoices(result.invoices);
    if (result.hourlyUsage && result.hourlyUsage.length > 0) setHourlyData(result.hourlyUsage);
    if (result.dailyUsage && result.dailyUsage.length > 0) setDailyData(result.dailyUsage);
    // Defined-but-empty is authoritative: keeping the previous months would leave rows the
    // portal no longer reports, and that this same sync just deleted from the device.
    if (result.monthlyUsage) setMonthlyData(result.monthlyUsage);

    const nowIso = new Date().toISOString();

    // The sync is only complete once the bundle is durable on the device: an awaited write is
    // what keeps a reload from coming back empty.
    await saveLocalSyncedBundle({
      account: result.account,
      places: result.places,
      selectedPlaceId: activePlaceId,
      invoices: result.invoices,
      hourlyUsage: result.hourlyUsage,
      dailyUsage: result.dailyUsage,
      monthlyUsage: result.monthlyUsage,
      lastSyncAt: nowIso
    });

    setLastSyncAt(nowIso);
    setBootState('ready');
  }, [selectedPlaceId]);

  // Auto-sync function executed automatically at 1:00 AM or when missed
  const trigger1AMAutoSync = useCallback(async (isScheduled = true) => {
    try {
      console.log(`[Auto-Sync] Executing 1:00 AM portal sync (isScheduled: ${isScheduled})`);
      const result = await executePortalScrape(undefined, lang);

      if (result.success) {
        // Same path as a manual sync, including the awaited device write.
        await handleSyncComplete(result);

        // Notify user about successful 1:00 AM auto-sync
        const syncMsgJa = '午前1:00の定時自動同期が完了しました。スマートメーター確定速報値と最新検針票を端末に保存しました。';
        const syncMsgEn = 'Scheduled 01:00 AM auto-sync completed. Latest 30-min telemetry and billing statements saved locally.';

        dispatchPushNotification(
          lang === 'ja' ? '午前1:00 定時自動同期完了' : '01:00 AM Auto-Sync Completed',
          lang === 'ja' ? syncMsgJa : syncMsgEn,
          'scrape_sync'
        );

        setAutoSyncToast(lang === 'ja' ? '01:00 自動同期完了 (ローカル保存済)' : '01:00 AM Auto-Sync Complete (Saved Locally)');
        setTimeout(() => setAutoSyncToast(null), 4000);
      }
    } catch (err) {
      console.warn('[Auto-Sync] Auto-sync failed or offline', err);
    }
  }, [lang, handleSyncComplete]);

  // Set up 1:00 AM scheduled auto-sync and missed sync check
  useEffect(() => {
    // 1. Check if we missed today's 1:00 AM sync while app was closed (only if account is synced)
    if (lastSyncAt && hasMissed1AMSyncToday(lastSyncAt)) {
      trigger1AMAutoSync(false);
    }

    // 2. Schedule next timer precisely for upcoming 1:00 AM
    let timerId: NodeJS.Timeout;
    const scheduleNext = () => {
      const msUntil1AM = getMillisecondsUntilNext1AM();
      console.log(`[Auto-Sync] Next 1:00 AM auto-sync scheduled in ${(msUntil1AM / (1000 * 60)).toFixed(1)} minutes`);

      timerId = setTimeout(() => {
        if (lastSyncAt) {
          trigger1AMAutoSync(true);
        }
        // Reschedule for next day 1:00 AM
        scheduleNext();
      }, msUntil1AM);
    };

    scheduleNext();

    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [lastSyncAt, trigger1AMAutoSync]);

  const handleToggleLanguage = () => {
    const nextLang: Language = lang === 'ja' ? 'en' : 'ja';
    setLang(nextLang);
    setSqliteLanguage(nextLang);
  };

  const handleBiometricPassed = () => {
    setIsLocked(false);
    setShowBiometricPrompt(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const handleRequireBiometrics = (onPassed: () => void, title?: string, subtitle?: string) => {
    const config = getBiometricConfig();
    if (!config.enabled) {
      onPassed();
      return;
    }
    setPendingAction(() => onPassed);
    setBiometricReason({
      title: title || (lang === 'ja' ? '電気料金のお支払い承認' : 'Payment Authorization'),
      subtitle: subtitle || (lang === 'ja' ? '生体認証で安全に決済を認証します' : 'Biometric verification required to confirm payment')
    });
    setShowBiometricPrompt(true);
  };

  const handleLockApp = () => {
    setPendingAction(null);
    setBiometricReason({
      title: lang === 'ja' ? 'アプリロック中' : 'App Locked',
      subtitle: lang === 'ja' ? 'シン・エナジーの保護データを表示するには生体認証を行ってください' : 'Authenticate to view Sym Energy data'
    });
    setIsLocked(true);
    setShowBiometricPrompt(true);
  };

  // Switch contracted places on navbar
  const handleSelectPlace = async (placeId: string) => {
    const updatedBundle = await selectContractedPlace(placeId, {
      ...bundle,
      account,
      places,
      selectedPlaceId,
      invoices,
      hourlyUsage: hourlyData,
      dailyUsage: dailyData,
      monthlyUsage: monthlyData,
      lastSyncAt,
      autoSyncEnabled: true
    });

    setBundle(updatedBundle);
    setSelectedPlaceId(updatedBundle.selectedPlaceId);
    setAccount(updatedBundle.account);
    setInvoices(updatedBundle.invoices);
    setHourlyData(updatedBundle.hourlyUsage);
    setDailyData(updatedBundle.dailyUsage);
    setMonthlyData(updatedBundle.monthlyUsage);
  };

  const handleSelectPlaceSafe = (placeId: string) => {
    handleSelectPlace(placeId).catch((err) => {
      console.error('[Place] Switching contracted place failed', err);
      setBootState('error');
    });
  };

  // Listen for bookmarklet broadcast messages or window focus with new telemetry
  useEffect(() => {
    const handleMessage = async (e: MessageEvent) => {
      if (e.data && e.data.type === 'SYMENERGY_TELEMETRY_SYNC' && e.data.payload) {
        try {
          const result = await importLocalTelemetry(e.data.payload);
          if (result.success) {
            await handleSyncComplete(result);
            setAutoSyncToast(
              lang === 'ja'
                ? '⚡ マイページから最新データを取り込みました！'
                : '⚡ Synced latest telemetry from portal!'
            );
            setTimeout(() => setAutoSyncToast(null), 5000);
          }
        } catch (err) {
          console.warn('[PostMessage] Ingest notice:', err);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleSyncComplete, lang]);

  const handleUpdateNotifications = (newNotifs: InAppNotification[]) => {
    setNotifications(newNotifs);
    saveStoredNotifications(newNotifs);
  };

  const handleResetCache = async () => {
    await resetSqliteDatabase();
    const freshBundle = await getSqliteSyncedBundle();
    setBundle(freshBundle);
    setAccount(freshBundle.account);
    setPlaces(freshBundle.places);
    setSelectedPlaceId(freshBundle.selectedPlaceId);
    setInvoices(freshBundle.invoices);
    setHourlyData(freshBundle.hourlyUsage);
    setDailyData(freshBundle.dailyUsage);
    setMonthlyData(freshBundle.monthlyUsage);
    setNotifications(getStoredNotifications());
    setLastSyncAt(freshBundle.lastSyncAt);
    setBootState(getSqliteInitFailure() ? 'error' : 'ready');
  };

  const unreadAlertsCount = notifications.filter((n) => !n.read).length;
  const nextSyncDisplay = getNext1AMDisplayString(lang);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-start items-center p-0 antialiased font-sans">
      {/* Biometric Full-screen Gate Modal */}
      <BiometricModal
        isOpen={showBiometricPrompt}
        onSuccess={handleBiometricPassed}
        title={biometricReason.title}
        subtitle={biometricReason.subtitle}
        canCancel={!isLocked}
        onCancel={() => {
          setShowBiometricPrompt(false);
          setPendingAction(null);
        }}
        lang={lang}
      />

      {/* PWA Home Screen Installation Modal */}
      <PWAInstallModal
        isOpen={showInstallModal}
        onClose={() => setShowInstallModal(false)}
        lang={lang}
      />

      {/* Main Clean Responsive Container (No iPhone bezel or wide view toggles) */}
      <div className="w-full max-w-4xl min-h-screen bg-slate-950 shadow-2xl flex flex-col relative border-x border-slate-900">
        {/* Offline Status Warning Bar (when internet connection is unavailable) */}
        {!isOnline && (
          <div className="bg-amber-950/90 border-b border-amber-800/80 px-4 py-2 text-xs text-amber-200 flex items-center justify-between gap-2 shadow-sm">
            <div className="flex items-center gap-2">
              <WifiOff className="w-4 h-4 text-amber-400 shrink-0" />
              <span>
                {lang === 'ja'
                  ? 'オフライン表示中：ローカル保存された同期データ（検針票・使用量グラフ）をすべて閲覧できます。'
                  : 'Offline Mode: All cached portal data (bills & 30-min charts) are fully viewable without internet.'}
              </span>
            </div>
            <span className="text-[10px] font-mono text-amber-400 shrink-0 hidden sm:inline">
              {lang === 'ja' ? '次回自動同期: 01:00' : 'Next Auto-Sync: 01:00'}
            </span>
          </div>
        )}

        {bootState === 'loading' && places.length === 0 && (
          <div className="bg-slate-900/90 border-b border-slate-800 px-4 py-2 text-xs text-slate-300 flex items-center gap-2">
            <span className="w-3 h-3 rounded-full border-2 border-teal-400 border-t-transparent animate-spin shrink-0" />
            <span>
              {lang === 'ja'
                ? '端末に保存された同期データを読み込んでいます...'
                : 'Loading the sync data saved on this device...'}
            </span>
          </div>
        )}

        {bootState === 'error' && (
          <div className="bg-rose-950/90 border-b border-rose-800/80 px-4 py-2 text-xs text-rose-200 flex items-center gap-2 shadow-sm">
            <ShieldAlert className="w-4 h-4 text-rose-400 shrink-0" />
            <span>
              {lang === 'ja'
                ? '端末内のデータベースを開けないため、保存済みの検針票・使用量データを表示できません。このサイトのストレージ設定を確認するか、もう一度同期してください。'
                : 'The on-device database could not be opened, so saved statements and usage charts cannot be restored yet. Check the storage settings for this site, then sync again.'}
            </span>
          </div>
        )}

        {/* 1:00 AM Auto-Sync Toast Notification */}
        {autoSyncToast && (
          <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 bg-teal-900/90 text-white border border-teal-500/50 px-4 py-2 rounded-2xl shadow-xl flex items-center gap-2 text-xs font-semibold animate-fadeIn backdrop-blur-md">
            <CheckCircle2 className="w-4 h-4 text-teal-300" />
            <span>{autoSyncToast}</span>
          </div>
        )}

        {/* Top Header Navigation with Contracted Places Selector on Navbar */}
        <TopNav
          account={account}
          places={places}
          selectedPlaceId={selectedPlaceId}
          onSelectPlace={handleSelectPlaceSafe}
          unreadCount={unreadAlertsCount}
          onOpenNotifications={() => setActiveTab('alerts')}
          onLockApp={handleLockApp}
          lang={lang}
          onToggleLanguage={handleToggleLanguage}
          isOffline={!isOnline}
          nextSyncDisplay={nextSyncDisplay}
          onOpenInstallModal={() => setShowInstallModal(true)}
        />

        {/* Scrollable Viewport Body */}
        <main className="flex-1 overflow-y-auto px-3 sm:px-5 pt-3 sm:pt-4 pb-24 scroll-smooth">
          {/* Active Tab Screen Routing */}
          {activeTab === 'usage' && (
            <UsageView
              account={account}
              hourlyData={hourlyData}
              dailyData={dailyData}
              monthlyData={monthlyData}
              tariff={account.tariff}
              onNavigateToScraper={() => setActiveTab('scraper')}
              lang={lang}
            />
          )}

          {activeTab === 'billing' && (
            <BillingView
              account={account}
              invoices={invoices}
              tariff={account.tariff}
              onUpdateInvoices={setInvoices}
              onRequireBiometrics={handleRequireBiometrics}
              lang={lang}
            />
          )}

          {activeTab === 'scraper' && (
            <ScraperView
              onSyncComplete={handleSyncComplete}
              lang={lang}
            />
          )}

          {activeTab === 'alerts' && (
            <NotificationCenter
              notifications={notifications}
              onUpdateNotifications={handleUpdateNotifications}
              lang={lang}
            />
          )}

          {activeTab === 'settings' && (
            <SettingsView
              account={account}
              tariff={account.tariff}
              hourlyData={hourlyData}
              invoices={invoices}
              onTriggerBiometricTest={() =>
                handleRequireBiometrics(
                  () => {},
                  lang === 'ja' ? '生体センサーテスト' : 'Biometric Sensor Test',
                  lang === 'ja' ? 'センサーおよびPINフォールバックが正常に稼働しています' : 'Biometric sensor and PIN fallback are fully operational'
                )
              }
              onResetCache={handleResetCache}
              lang={lang}
              onOpenInstallModal={() => setShowInstallModal(true)}
            />
          )}
        </main>

        {/* Fixed Ergonomic Bottom Tab Bar */}
        <BottomTabBar
          activeTab={activeTab}
          onChangeTab={setActiveTab}
          unreadAlertsCount={unreadAlertsCount}
          lang={lang}
        />
      </div>
    </div>
  );
}
